import {
  Box,
  Fade,
  InputAdornment,
  Popper,
  SvgIcon,
  ClickAwayListener,
  IconButton,
} from '@mui/material';
import { useState, FunctionComponent } from 'react';
import { DateRange, RangeKeyDict } from 'react-date-range';
import { useTranslation } from 'react-i18next';

import TextField from '@Components/TextField';
import Icons from '@Constants/Icons.constants';
import { getDateMMDDYYYYHHMM } from '@Utils/common/common.utils';

import { DateRangePickerProps } from './DateRangePicker.types';

import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import './DateRangePicker.styles.scss'; // custom css

const DateRangePicker: FunctionComponent<DateRangePickerProps> = (
  props: DateRangePickerProps
): JSX.Element => {
  const { t } = useTranslation();
  const { dateState, setDateState, disabled, getDateState } = props;

  /** STATES */
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  /** HANDLES CLICK ON DATE RANGE TEXTFIELD */
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((previousOpen) => !previousOpen);
  };

  /** HANDLES CLICK OUTSIDE OF DATE RANGE (closes the dropdown) */
  const handleClickAway = () => {
    setOpen(false);
  };

  /** ON DATE RANGE SELECT */
  const onChange = (range: RangeKeyDict) => {
    const { startDate, endDate } = range.selection;

    setDateState([range.selection]);

    if (getDateState) {
      getDateState(startDate, endDate);
    }
  };

  /** HANDLES CLEAR ICON CLICK */
  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    handleClickAway();
    setDateState((prevState) => [
      {
        ...prevState[0],
        startDate: undefined,
        endDate: undefined,
      },
    ]);

    if (getDateState) {
      getDateState(undefined, undefined);
    }
  };

  /** Date With Format (mm/dd/yyy) */
  const formattedDate =
    dateState[0].startDate && dateState[0].endDate
      ? `${getDateMMDDYYYYHHMM(
          dateState[0].startDate,
          true
        )}-${getDateMMDDYYYYHHMM(dateState[0].endDate, true)}`
      : '';

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box>
          <TextField
            focused={open}
            disabled={disabled}
            onClick={!disabled ? handleClick : () => {}}
            placeholder={t('common.dateRange.Enter a date range')}
            size="small"
            value={formattedDate}
            sx={{
              '&:hover': {
                '& .clearBtn': {
                  display: 'flex',
                },
              },
              '& .MuiInputBase-input': {
                pr: 0,
                maxWidth: '17rem',
              },
              '& .MuiOutlinedInput-root, & .MuiOutlinedInput-input': {
                cursor: !disabled ? 'pointer' : 'not-allowed',
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ m: 0, cursor: !disabled ? 'pointer' : 'not-allowed' }}
                >
                  <Box minWidth="3.6rem">
                    {(dateState[0].startDate || dateState[0].endDate) && (
                      <IconButton
                        className="clearBtn"
                        sx={{
                          display: 'none',
                          m: 0,
                          p: 0.4,
                          ml: 0.4,
                        }}
                        onClick={handleClear}
                      >
                        <SvgIcon>{Icons.close}</SvgIcon>
                      </IconButton>
                    )}
                  </Box>
                  <SvgIcon
                    sx={{
                      mr: 1.2,
                      ml: 0.8,
                      color: !disabled
                        ? ({ colors }) =>
                            open ? 'secondary.main' : colors.inputText
                        : 'action.disabled',
                    }}
                  >
                    {Icons.dateRange}
                  </SvgIcon>
                </InputAdornment>
              ),
              inputProps: {
                disabled: true,
              },
            }}
          />
          <Popper
            open={open}
            anchorEl={anchorEl}
            transition
            disablePortal
            style={{ zIndex: 100 }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <Box>
                  <DateRange
                    editableDateInputs
                    onChange={onChange}
                    moveRangeOnFirstSelection={false}
                    ranges={dateState}
                    showDateDisplay={false}
                    maxDate={new Date()}
                  />
                </Box>
              </Fade>
            )}
          </Popper>
        </Box>
      </ClickAwayListener>
    </>
  );
};

export default DateRangePicker;
